import { supabase } from '../lib/supabase';
import type {
  Team,
  Player,
  PlayerClassification,
  Event,
  EventStatus,
  Discipline,
  DisciplineVariant,
  Squad,
  SquadPlayer,
  Round,
  PlayerRound,
  Shot,
  StationStats,
  TrajectoryStats,
  TimeRangeStats,
  Trajectory,
  ParentPlayerLink,
  PlayerGoal,
  CoachingNote,
  SavedSquad,
  AdminActivity,
  ScoringFeedback,
} from '../types';

// Shared event mapper — single source of truth for DB row → Event
const mapEvent = (data: any): Event => ({
  id: data.id,
  teamId: data.team_id,
  coachId: data.created_by || '',
  name: data.name,
  isPracticeEvent: data.is_practice_event,
  discipline: data.discipline || 'trap',
  variant: data.variant || 'american-trap',
  location: data.location || undefined,
  status: (data.status as EventStatus) || 'active',
  scheduledStart: data.scheduled_start ? new Date(data.scheduled_start).getTime() : null,
  eventCode: data.event_code || undefined,
  defaultHandicapYardage: data.default_handicap_yardage ?? undefined,
  lastActivityAt: data.last_activity_at ? new Date(data.last_activity_at).getTime() : undefined,
  lastScoredBy: data.last_scored_by || undefined,
  closedBy: data.closed_by || undefined,
  closeReason: data.close_reason || undefined,
  archivedAt: data.archived_at ? new Date(data.archived_at).getTime() : null,
  practiceObjective: data.practice_objective || undefined,
  drillTags: data.drill_tags || [],
  startTime: data.start_time ? new Date(data.start_time).getTime() : 0,
  endTime: data.end_time ? new Date(data.end_time).getTime() : null,
  createdAt: new Date(data.created_at).getTime(),
});

const mapPlayer = (data: any): Player => ({
  id: data.id,
  profileId: data.profile_id || undefined,
  name: data.name,
  dateOfBirth: data.date_of_birth || '',
  classification: data.classification || undefined,
  status: data.status || 'active',
  archivedAt: data.archived_at ? new Date(data.archived_at).getTime() : null,
  mergedIntoId: data.merged_into_id || undefined,
  claimedAt: data.claimed_at ? new Date(data.claimed_at).getTime() : null,
  createdAt: new Date(data.created_at).getTime(),
});

const mapShot = (data: any): Shot => ({
  id: data.id,
  clientMutationId: data.client_mutation_id || data.id,
  playerRoundId: data.player_round_id,
  station: data.station,
  shotNumber: data.shot_number,
  result: data.result,
  trajectory: data.trajectory,
  coachingTip: data.coaching_tip || null,
  timestamp: new Date(data.timestamp).getTime(),
});

// Team operations
export const createTeam = async (name: string, createdByProfileId: string): Promise<Team> => {
  const { data, error } = await supabase
    .from('teams')
    .insert([{
      name,
      created_by: createdByProfileId,
      visibility: 'private',
      stats_visibility: 'team-only',
    }])
    .select()
    .single();

  if (error) throw error;

  // Team creators administer the team; head coach is assigned explicitly in the roster.
  const { error: memberError } = await supabase
    .from('team_members')
    .insert([{
      team_id: data.id,
      profile_id: createdByProfileId,
      role: 'coach',
      status: 'active',
      is_admin: true,
      is_head_coach: false,
    }]);

  if (memberError) {
    // Don't throw - team is created, membership add can be retried
  }

  return {
    id: data.id,
    name: data.name,
    createdAt: new Date(data.created_at).getTime(),
  };
};

export const getAllTeams = async (): Promise<Team[]> => {
  // Get teams the user has access to (created by them or they're a member of)
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;

  return data.map(team => ({
    id: team.id,
    name: team.name,
    createdAt: new Date(team.created_at).getTime(),
  }));
};

export const getTeamById = async (id: string): Promise<Team | undefined> => {
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('id', id)
    .single();

  if (error) return undefined;

  return {
    id: data.id,
    name: data.name,
    createdAt: new Date(data.created_at).getTime(),
  };
};

// Coach operations (now team_members with role='coach')
export const getCoachesByTeam = async (teamId: string): Promise<Array<{
  id: string;
  profileId: string;
  name: string;
  teamId: string;
  isHeadCoach: boolean;
  isAdmin: boolean;
  createdAt: number;
}>> => {
  const { data, error } = await supabase
    .from('team_members')
    .select(`
      id,
      profile_id,
      team_id,
      joined_at,
      is_head_coach,
      is_admin,
      profiles!inner(display_name)
    `)
    .eq('team_id', teamId)
    .eq('role', 'coach')
    .eq('status', 'active');

  if (error) throw error;

  return data.map(member => ({
    id: member.id,
    profileId: member.profile_id,
    name: (member.profiles as any).display_name,
    teamId: member.team_id,
    isHeadCoach: member.is_head_coach ?? false,
    isAdmin: member.is_admin ?? false,
    createdAt: new Date(member.joined_at).getTime(),
  }));
};

// Player operations
export const createPlayer = async (name: string, dateOfBirth: string, profileId: string, teamId?: string, classification?: PlayerClassification): Promise<Player> => {
  const { data, error } = await supabase
    .from('players')
    .insert([{
      profile_id: profileId,
      name,
      date_of_birth: dateOfBirth || null, // Convert empty string to null
      team_id: teamId || null,
      classification: classification || null,
    }])
    .select()
    .single();

  if (error) throw error;

  return mapPlayer(data);
};

export const getAllPlayers = async (): Promise<Player[]> => {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .order('name', { ascending: true });

  if (error) throw error;

  return data.map(mapPlayer);
};

export const getPlayersByProfile = async (profileId: string): Promise<Player[]> => {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('profile_id', profileId)
    .order('name', { ascending: true });

  if (error) throw error;

  return data.map(mapPlayer);
};

export const getPlayersByTeam = async (teamId: string): Promise<Player[]> => {
  const [{ data, error }, { data: coachMembers }] = await Promise.all([
    supabase
      .from('players')
      .select('*')
      .eq('team_id', teamId)
      .eq('status', 'active')
      .order('name', { ascending: true }),
    supabase
      .from('team_members')
      .select('profile_id, profiles!inner(display_name)')
      .eq('team_id', teamId)
      .eq('role', 'coach')
      .eq('status', 'active'),
  ]);

  if (error) throw error;

  // Filter out the coach's own player record (where profile_id AND name both match).
  // This avoids hiding players that a coach manually added (same profile_id, different name).
  const coachIdentities = new Set(
    (coachMembers || []).map(c => `${c.profile_id}::${(c.profiles as any).display_name}`)
  );

  return data
    .filter(player => !player.profile_id || !coachIdentities.has(`${player.profile_id}::${player.name}`))
    .map(mapPlayer);
};

export const getPlayerById = async (id: string): Promise<Player | undefined> => {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('id', id)
    .single();

  if (error) return undefined;

  return mapPlayer(data);
};

export const updatePlayer = async (id: string, updates: Partial<Player>): Promise<void> => {
  const dbUpdates: any = {};
  if (updates.name) dbUpdates.name = updates.name;
  if (updates.dateOfBirth !== undefined) {
    dbUpdates.date_of_birth = updates.dateOfBirth || null; // Convert empty string to null
  }
  if (updates.classification !== undefined) {
    dbUpdates.classification = updates.classification || null;
  }

  const { error } = await supabase
    .from('players')
    .update(dbUpdates)
    .eq('id', id);

  if (error) throw error;
};

export const deletePlayer = async (id: string): Promise<void> => {
  const { data, error } = await supabase
    .from('players')
    .delete()
    .eq('id', id)
    .select('id')
    .single();

  if (error) throw error;
  if (!data) throw new Error('Shooter could not be deleted.');
};

export const getArchivedPlayersByTeam = async (teamId: string): Promise<Player[]> => {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('team_id', teamId)
    .eq('status', 'archived')
    .is('merged_into_id', null)
    .order('name');
  if (error) throw error;
  return (data || []).map(mapPlayer);
};

export const archivePlayer = async (id: string, archived: boolean): Promise<void> => {
  const { error } = await supabase
    .from('players')
    .update({ status: archived ? 'archived' : 'active', archived_at: archived ? new Date().toISOString() : null })
    .eq('id', id);
  if (error) throw error;
};

export const mergePlayers = async (keepPlayerId: string, mergePlayerId: string, actorProfileId: string): Promise<void> => {
  const { error } = await supabase.rpc('merge_players_preserving_history', {
    p_keep_player_id: keepPlayerId,
    p_merge_player_id: mergePlayerId,
    p_actor_profile_id: actorProfileId,
  });
  if (error) throw error;
};

// Event operations
export const createEvent = async (
  teamId: string,
  coachId: string,
  name?: string,
  isPracticeEvent: boolean = false,
  scorekeeperProfileId?: string,
  discipline: Discipline = 'trap',
  variant: DisciplineVariant = 'american-trap',
  location?: string,
  scheduledStart?: string,
  defaultHandicapYardage?: number
): Promise<Event> => {
  // Defense-in-depth: server-side subscription check
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: subCheck } = await supabase.rpc('check_event_creation_allowed', {
        p_user_id: user.id,
      });
      if (subCheck && !subCheck.allowed) {
        throw new Error('Event creation limit reached. Please subscribe to DeadBird Pro for unlimited events.');
      }
    }
  } catch (e: any) {
    // Re-throw limit errors, swallow RPC failures (fail open)
    if (e?.message?.includes('limit reached')) throw e;
  }

  const defaultName = isPracticeEvent
    ? `Practice ${new Date().toLocaleDateString()}`
    : `Event ${new Date().toLocaleDateString()}`;

  const isScheduled = !!scheduledStart;

  const { data, error } = await supabase
    .from('events')
    .insert([{
      team_id: teamId,
      name: name || defaultName,
      is_practice_event: isPracticeEvent,
      scorekeeper_id: scorekeeperProfileId,
      created_by: scorekeeperProfileId,
      visibility: 'team-only',
      discipline,
      variant,
      location: location || null,
      status: isScheduled ? 'scheduled' : 'active',
      scheduled_start: scheduledStart || null,
      start_time: isScheduled ? null : new Date().toISOString(),
      default_handicap_yardage: defaultHandicapYardage || null,
    }])
    .select()
    .single();

  if (error) throw error;

  return mapEvent(data);
};

export const createPractice = createEvent;

export const endEvent = async (eventId: string, closedBy?: string, reason?: string): Promise<void> => {
  const { data, error } = await supabase
    .from('events')
    .update({
      end_time: new Date().toISOString(),
      status: 'completed',
      closed_by: closedBy || null,
      close_reason: reason || null,
    })
    .eq('id', eventId)
    .select('id')
    .single();

  if (error) throw error;
  if (!data) throw new Error('Event could not be closed.');
};

export const endPractice = endEvent;

export const cancelEvent = async (eventId: string, closedBy?: string, reason?: string): Promise<void> => {
  const { data, error } = await supabase
    .from('events')
    .update({
      end_time: new Date().toISOString(),
      status: 'canceled',
      closed_by: closedBy || null,
      close_reason: reason || 'Canceled',
    })
    .eq('id', eventId)
    .select('id')
    .single();
  if (error) throw error;
  if (!data) throw new Error('Event could not be canceled.');
};

export const archiveEvent = async (eventId: string, archived: boolean): Promise<void> => {
  const { error } = await supabase
    .from('events')
    .update({ archived_at: archived ? new Date().toISOString() : null })
    .eq('id', eventId);
  if (error) throw error;
};

export const updateEventPracticePlan = async (eventId: string, objective: string, drillTags: string[]): Promise<void> => {
  const { error } = await supabase.from('events').update({
    practice_objective: objective.trim() || null,
    drill_tags: drillTags,
  }).eq('id', eventId);
  if (error) throw error;
};

export const getActiveEvent = async (): Promise<Event | undefined> => {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('status', 'active')
    .single();

  if (error) return undefined;

  return mapEvent(data);
};

export const getActivePractice = getActiveEvent;

export const getEventById = async (id: string): Promise<Event | undefined> => {
  const { data, error} = await supabase
    .from('events')
    .select('*')
    .eq('id', id)
    .single();

  if (error) return undefined;

  return mapEvent(data);
};

export const getPracticeById = getEventById;

export const getAllEvents = async (): Promise<Event[]> => {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .order('start_time', { ascending: false });

  if (error) throw error;

  return data.map(mapEvent);
};

export const getAllPractices = getAllEvents;

export const getEventsByTeam = async (teamId: string): Promise<Event[]> => {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('team_id', teamId)
    .is('archived_at', null)
    .order('start_time', { ascending: false });

  if (error) throw error;

  return data.map(mapEvent);
};

export const getPracticesByTeam = getEventsByTeam;

export const getActiveEventsByTeam = async (teamId: string): Promise<Event[]> => {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('team_id', teamId)
    .eq('status', 'active')
    .is('archived_at', null)
    .order('start_time', { ascending: false });

  if (error) throw error;

  return data.map(mapEvent);
};

export const getActiveEventCountByTeam = async (teamId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('events')
    .select('*', { count: 'exact', head: true })
    .eq('team_id', teamId)
    .eq('status', 'active');

  if (error) return 0;
  return count ?? 0;
};

// Squad operations
export const createSquad = async (eventId: string, name: string): Promise<Squad> => {
  const { data, error } = await supabase
    .from('squads')
    .insert([{
      event_id: eventId,
      name,
    }])
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    eventId: data.event_id,
    name: data.name,
    createdAt: new Date(data.created_at).getTime(),
  };
};

export const getSquadsByEvent = async (eventId: string): Promise<Squad[]> => {
  const { data, error } = await supabase
    .from('squads')
    .select('*')
    .eq('event_id', eventId);

  if (error) throw error;

  return data.map(squad => ({
    id: squad.id,
    eventId: squad.event_id,
    name: squad.name,
    createdAt: new Date(squad.created_at).getTime(),
  }));
};

export const getSquadsByPractice = getSquadsByEvent;

// SquadPlayer operations
export const addPlayerToSquad = async (
  squadId: string,
  playerId: string,
  station: number,
  handicapYardage?: number
): Promise<SquadPlayer> => {
  const { data, error } = await supabase
    .from('squad_players')
    .insert([{
      squad_id: squadId,
      player_id: playerId,
      station,
      handicap_yardage: handicapYardage || null,
    }])
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    squadId: data.squad_id,
    playerId: data.player_id,
    station: data.station,
    handicapYardage: data.handicap_yardage ?? undefined,
    createdAt: new Date(data.created_at).getTime(),
  };
};

export const getSquadPlayers = async (squadId: string): Promise<SquadPlayer[]> => {
  const { data, error } = await supabase
    .from('squad_players')
    .select('*')
    .eq('squad_id', squadId)
    .order('station', { ascending: true });

  if (error) throw error;

  return data.map(sp => ({
    id: sp.id,
    squadId: sp.squad_id,
    playerId: sp.player_id,
    station: sp.station,
    handicapYardage: sp.handicap_yardage ?? undefined,
    createdAt: new Date(sp.created_at).getTime(),
  }));
};

// Round operations
export const createRound = async (
  squadId: string,
  roundNumber: number,
  shotsPerStation: number = 5
): Promise<Round> => {
  const { data, error } = await supabase
    .from('rounds')
    .insert([{
      squad_id: squadId,
      round_number: roundNumber,
      shots_per_station: shotsPerStation,
    }])
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    squadId: data.squad_id,
    roundNumber: data.round_number,
    shotsPerStation: data.shots_per_station,
    createdAt: new Date(data.created_at).getTime(),
  };
};

export const getRoundsBySquad = async (squadId: string): Promise<Round[]> => {
  const { data, error } = await supabase
    .from('rounds')
    .select('*')
    .eq('squad_id', squadId)
    .order('round_number', { ascending: true });

  if (error) throw error;

  return data.map(round => ({
    id: round.id,
    squadId: round.squad_id,
    roundNumber: round.round_number,
    shotsPerStation: round.shots_per_station,
    createdAt: new Date(round.created_at).getTime(),
  }));
};

// PlayerRound operations
export const createPlayerRound = async (
  roundId: string,
  squadPlayerId: string,
  currentStation: number
): Promise<PlayerRound> => {
  const { data, error } = await supabase
    .from('player_rounds')
    .insert([{
      round_id: roundId,
      squad_player_id: squadPlayerId,
      current_station: currentStation,
    }])
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    roundId: data.round_id,
    squadPlayerId: data.squad_player_id,
    currentStation: data.current_station,
    createdAt: new Date(data.created_at).getTime(),
  };
};

export const getPlayerRoundsByRound = async (roundId: string): Promise<PlayerRound[]> => {
  const { data, error } = await supabase
    .from('player_rounds')
    .select('*')
    .eq('round_id', roundId);

  if (error) throw error;

  return data.map(pr => ({
    id: pr.id,
    roundId: pr.round_id,
    squadPlayerId: pr.squad_player_id,
    currentStation: pr.current_station,
    createdAt: new Date(pr.created_at).getTime(),
  }));
};

export const updatePlayerRoundStation = async (
  playerRoundId: string,
  station: number
): Promise<void> => {
  const { error } = await supabase
    .from('player_rounds')
    .update({ current_station: station })
    .eq('id', playerRoundId);

  if (error) throw error;
};

// Shot operations
export const createShot = async (shot: Omit<Shot, 'id'> & { id?: string }): Promise<Shot> => {
  const id = shot.id || crypto.randomUUID();
  const clientMutationId = shot.clientMutationId || id;
  const { data, error } = await supabase
    .from('shots')
    .upsert([{
      id,
      client_mutation_id: clientMutationId,
      player_round_id: shot.playerRoundId,
      station: shot.station,
      shot_number: shot.shotNumber,
      result: shot.result,
      trajectory: shot.trajectory,
      coaching_tip: shot.coachingTip || null,
      timestamp: new Date(shot.timestamp).toISOString(),
    }], { onConflict: 'client_mutation_id' })
    .select()
    .single();

  if (error) throw error;

  return mapShot(data);
};

export const createShots = async (shots: Array<Omit<Shot, 'id'> & { id?: string }>): Promise<Shot[]> => {
  if (shots.length === 0) return [];

  const { data, error } = await supabase
    .from('shots')
    .upsert(shots.map(shot => {
      const id = shot.id || crypto.randomUUID();
      return {
      id,
      client_mutation_id: shot.clientMutationId || id,
      player_round_id: shot.playerRoundId,
      station: shot.station,
      shot_number: shot.shotNumber,
      result: shot.result,
      trajectory: shot.trajectory,
      coaching_tip: shot.coachingTip || null,
      timestamp: new Date(shot.timestamp).toISOString(),
    };}), { onConflict: 'client_mutation_id' })
    .select();

  if (error) throw error;

  return data.map(mapShot);
};

export const upsertQueuedShots = async (shots: Shot[]): Promise<Shot[]> => createShots(shots);

export const getShotsByPlayerRound = async (playerRoundId: string): Promise<Shot[]> => {
  const { data, error } = await supabase
    .from('shots')
    .select('*')
    .eq('player_round_id', playerRoundId)
    .order('timestamp', { ascending: true });

  if (error) throw error;

  return data.map(mapShot);
};

export const deleteLastShot = async (playerRoundId: string): Promise<Shot | undefined> => {
  const shots = await getShotsByPlayerRound(playerRoundId);
  if (shots.length === 0) return undefined;

  const lastShot = shots[shots.length - 1];

  const { error } = await supabase
    .from('shots')
    .delete()
    .eq('id', lastShot.id);

  if (error) throw error;

  return lastShot;
};

export const deleteShotById = async (shotId: string): Promise<void> => {
  const { error } = await supabase
    .from('shots')
    .delete()
    .eq('id', shotId);

  if (error) throw error;
};

export const getAllShotsByPractice = async (practiceId: string): Promise<Shot[]> => {
  // This requires joining multiple tables
  const { data, error } = await supabase
    .from('shots')
    .select(`
      *,
      player_rounds!inner(
        *,
        rounds!inner(
          *,
          squads!inner(
            event_id
          )
        )
      )
    `)
    .eq('player_rounds.rounds.squads.event_id', practiceId)
    .order('timestamp', { ascending: true });

  if (error) throw error;

  return data.map(shot => ({
    id: shot.id,
    playerRoundId: shot.player_round_id,
    station: shot.station,
    shotNumber: shot.shot_number,
    result: shot.result,
    trajectory: shot.trajectory,
    coachingTip: shot.coaching_tip || null,
    timestamp: new Date(shot.timestamp).getTime(),
  }));
};

// Player Statistics Operations
export const getShotsByPlayer = async (
  playerId: string,
  startDate?: number,
  endDate?: number
): Promise<Shot[]> => {
  let query = supabase
    .from('shots')
    .select(`
      *,
      player_rounds!inner(
        squad_player_id,
        rounds(id)
      )
    `);

  // Get squad_players for this player
  const { data: squadPlayers } = await supabase
    .from('squad_players')
    .select('id')
    .eq('player_id', playerId);

  if (!squadPlayers || squadPlayers.length === 0) return [];

  const squadPlayerIds = squadPlayers.map(sp => sp.id);

  const { data, error } = await query;

  if (error) throw error;

  // Filter shots by squad player IDs and date range
  let filteredData = data.filter((shot: any) =>
    squadPlayerIds.includes(shot.player_rounds.squad_player_id)
  );

  if (startDate) {
    filteredData = filteredData.filter((shot: any) =>
      new Date(shot.timestamp).getTime() >= startDate
    );
  }

  if (endDate) {
    filteredData = filteredData.filter((shot: any) =>
      new Date(shot.timestamp).getTime() <= endDate
    );
  }

  return filteredData.map((shot: any) => ({
    id: shot.id,
    playerRoundId: shot.player_round_id,
    station: shot.station,
    shotNumber: shot.shot_number,
    result: shot.result,
    trajectory: shot.trajectory,
    coachingTip: shot.coaching_tip || null,
    timestamp: new Date(shot.timestamp).getTime(),
  }));
};

export const getStationStats = async (
  playerId: string,
  startDate?: number,
  endDate?: number
): Promise<StationStats[]> => {
  const shots = await getShotsByPlayer(playerId, startDate, endDate);

  const statsByStation = new Map<number, { hits: number; misses: number }>();

  // Initialize all stations
  for (let station = 1; station <= 5; station++) {
    statsByStation.set(station, { hits: 0, misses: 0 });
  }

  // Count hits and misses
  for (const shot of shots) {
    const stats = statsByStation.get(shot.station);
    if (stats) {
      if (shot.result === 'hit') {
        stats.hits++;
      } else {
        stats.misses++;
      }
    }
  }

  // Convert to array
  const stationStats: StationStats[] = [];
  for (let station = 1; station <= 5; station++) {
    const stats = statsByStation.get(station)!;
    const total = stats.hits + stats.misses;
    stationStats.push({
      station,
      hits: stats.hits,
      misses: stats.misses,
      total,
      percentage: total > 0 ? Math.round((stats.hits / total) * 100) : 0,
    });
  }

  return stationStats;
};

export const getTrajectoryStats = async (
  playerId: string,
  startDate?: number,
  endDate?: number
): Promise<TrajectoryStats[]> => {
  const shots = await getShotsByPlayer(playerId, startDate, endDate);

  const trajectories: Trajectory[] = ['hard-left', 'left', 'middle', 'right', 'hard-right'];
  const statsByTrajectory = new Map<Trajectory, { hits: number; misses: number }>();

  // Initialize all trajectories
  for (const traj of trajectories) {
    statsByTrajectory.set(traj, { hits: 0, misses: 0 });
  }

  // Count hits and misses
  for (const shot of shots) {
    const stats = statsByTrajectory.get(shot.trajectory);
    if (stats) {
      if (shot.result === 'hit') {
        stats.hits++;
      } else {
        stats.misses++;
      }
    }
  }

  // Convert to array
  const trajectoryStats: TrajectoryStats[] = [];
  for (const traj of trajectories) {
    const stats = statsByTrajectory.get(traj)!;
    const total = stats.hits + stats.misses;
    trajectoryStats.push({
      trajectory: traj,
      hits: stats.hits,
      misses: stats.misses,
      total,
      percentage: total > 0 ? Math.round((stats.hits / total) * 100) : 0,
    });
  }

  return trajectoryStats;
};

export const getStationTrajectoryStats = async (
  playerId: string,
  station: number,
  startDate?: number,
  endDate?: number
): Promise<TrajectoryStats[]> => {
  const shots = await getShotsByPlayer(playerId, startDate, endDate);
  const stationShots = shots.filter(s => s.station === station);

  const trajectories: Trajectory[] = ['hard-left', 'left', 'middle', 'right', 'hard-right'];
  const statsByTrajectory = new Map<Trajectory, { hits: number; misses: number }>();

  // Initialize all trajectories
  for (const traj of trajectories) {
    statsByTrajectory.set(traj, { hits: 0, misses: 0 });
  }

  // Count hits and misses for this station
  for (const shot of stationShots) {
    const stats = statsByTrajectory.get(shot.trajectory);
    if (stats) {
      if (shot.result === 'hit') {
        stats.hits++;
      } else {
        stats.misses++;
      }
    }
  }

  // Convert to array
  const trajectoryStats: TrajectoryStats[] = [];
  for (const traj of trajectories) {
    const stats = statsByTrajectory.get(traj)!;
    const total = stats.hits + stats.misses;
    trajectoryStats.push({
      trajectory: traj,
      hits: stats.hits,
      misses: stats.misses,
      total,
      percentage: total > 0 ? Math.round((stats.hits / total) * 100) : 0,
    });
  }

  return trajectoryStats;
};

export const getTimeRangeStats = async (
  playerId: string,
  startDate?: number,
  endDate?: number
): Promise<TimeRangeStats> => {
  const shots = await getShotsByPlayer(playerId, startDate, endDate);
  const hits = shots.filter(s => s.result === 'hit').length;
  const misses = shots.filter(s => s.result === 'miss').length;
  const total = shots.length;

  const byStation = await getStationStats(playerId, startDate, endDate);
  const byTrajectory = await getTrajectoryStats(playerId, startDate, endDate);

  return {
    startDate: startDate || (shots[0]?.timestamp || Date.now()),
    endDate: endDate || Date.now(),
    totalShots: total,
    hits,
    misses,
    percentage: total > 0 ? Math.round((hits / total) * 100) : 0,
    byStation,
    byTrajectory,
  };
};

export const getEventsByPlayer = async (playerId: string): Promise<Event[]> => {
  const { data: squadPlayers } = await supabase
    .from('squad_players')
    .select('squad_id')
    .eq('player_id', playerId);

  if (!squadPlayers || squadPlayers.length === 0) return [];

  const squadIds = squadPlayers.map(sp => sp.squad_id);

  const { data: squads } = await supabase
    .from('squads')
    .select('event_id')
    .in('id', squadIds);

  if (!squads || squads.length === 0) return [];

  const eventIds = [...new Set(squads.map(s => s.event_id))];

  const { data, error } = await supabase
    .from('events')
    .select('*')
    .in('id', eventIds)
    .order('start_time', { ascending: false });

  if (error) throw error;

  return data.map(mapEvent);
};

export const getPracticesByPlayer = getEventsByPlayer;

export const getPlayerAccuracyOverTime = async (
  playerId: string,
  station?: number
): Promise<Array<{ eventId: string; eventName: string; date: number; percentage: number; hits: number; total: number }>> => {
  try {
    // First get player_round_ids for this player
    const { data: playerRounds, error: prError } = await supabase
      .from('player_rounds')
      .select(`
        id,
        round_id,
        squad_player_id,
        squad_players!inner(player_id)
      `)
      .eq('squad_players.player_id', playerId);

    if (prError) return [];
    if (!playerRounds || playerRounds.length === 0) return [];

    const playerRoundIds = playerRounds.map((pr: any) => pr.id);

    // Now get all shots for these player_rounds with event info
    const { data: shots, error } = await supabase
      .from('shots')
      .select(`
        result,
        station,
        player_round_id,
        player_rounds!inner(
          rounds!inner(
            squads!inner(
              events!inner(
                id,
                name,
                start_time
              )
            )
          )
        )
      `)
      .in('player_round_id', playerRoundIds);

    if (error) return [];
    if (!shots || shots.length === 0) return [];

    // Group shots by event
    const eventMap = new Map<string, { name: string; startTime: number; hits: number; total: number }>();

    for (const shot of shots as any[]) {
      // Filter by station if specified
      if (station !== undefined && shot.station !== station) {
        continue;
      }

      const event = shot.player_rounds?.rounds?.squads?.events;
      if (!event) continue;

      const eventId = event.id;
      if (!eventMap.has(eventId)) {
        eventMap.set(eventId, {
          name: event.name || 'Untitled Event',
          startTime: new Date(event.start_time).getTime(),
          hits: 0,
          total: 0,
        });
      }

      const eventData = eventMap.get(eventId)!;
      eventData.total++;
      if (shot.result === 'hit') {
        eventData.hits++;
      }
    }

    // Convert to array format
    const accuracyData = Array.from(eventMap.entries()).map(([eventId, data]) => ({
      eventId,
      eventName: data.name,
      date: data.startTime,
      percentage: data.total > 0 ? Math.round((data.hits / data.total) * 100) : 0,
      hits: data.hits,
      total: data.total,
    }));

    return accuracyData.sort((a, b) => a.date - b.date);
  } catch {
    return [];
  }
};

export const getPlayerEventStats = async (
  playerId: string,
  eventId: string
): Promise<TimeRangeStats> => {
  // Get all squads for this event
  const { data: squads } = await supabase
    .from('squads')
    .select('id')
    .eq('event_id', eventId);

  if (!squads || squads.length === 0) {
    return {
      startDate: Date.now(),
      endDate: Date.now(),
      totalShots: 0,
      hits: 0,
      misses: 0,
      percentage: 0,
      byStation: [],
      byTrajectory: [],
    };
  }

  const squadIds = squads.map(s => s.id);

  // Get squad players for this player in these squads
  const { data: squadPlayers } = await supabase
    .from('squad_players')
    .select('id')
    .eq('player_id', playerId)
    .in('squad_id', squadIds);

  if (!squadPlayers || squadPlayers.length === 0) {
    return {
      startDate: Date.now(),
      endDate: Date.now(),
      totalShots: 0,
      hits: 0,
      misses: 0,
      percentage: 0,
      byStation: [],
      byTrajectory: [],
    };
  }

  const squadPlayerIds = squadPlayers.map(sp => sp.id);

  // Get all player rounds for these squad players
  const { data: playerRounds } = await supabase
    .from('player_rounds')
    .select('id')
    .in('squad_player_id', squadPlayerIds);

  if (!playerRounds || playerRounds.length === 0) {
    return {
      startDate: Date.now(),
      endDate: Date.now(),
      totalShots: 0,
      hits: 0,
      misses: 0,
      percentage: 0,
      byStation: [],
      byTrajectory: [],
    };
  }

  const playerRoundIds = playerRounds.map(pr => pr.id);

  // Get all shots for these player rounds
  const { data: shots } = await supabase
    .from('shots')
    .select('*')
    .in('player_round_id', playerRoundIds)
    .order('timestamp', { ascending: true });

  if (!shots || shots.length === 0) {
    return {
      startDate: Date.now(),
      endDate: Date.now(),
      totalShots: 0,
      hits: 0,
      misses: 0,
      percentage: 0,
      byStation: [],
      byTrajectory: [],
    };
  }

  const allShots = shots.map(shot => ({
    id: shot.id,
    playerRoundId: shot.player_round_id,
    station: shot.station,
    shotNumber: shot.shot_number,
    result: shot.result,
    trajectory: shot.trajectory,
    coachingTip: shot.coaching_tip || null,
    timestamp: new Date(shot.timestamp).getTime(),
  }));

  const hits = allShots.filter(s => s.result === 'hit').length;
  const misses = allShots.filter(s => s.result === 'miss').length;
  const total = allShots.length;

  // Calculate station stats
  const stationStatsMap = new Map<number, { hits: number; misses: number }>();
  for (let station = 1; station <= 5; station++) {
    stationStatsMap.set(station, { hits: 0, misses: 0 });
  }
  for (const shot of allShots) {
    const stats = stationStatsMap.get(shot.station);
    if (stats) {
      if (shot.result === 'hit') {
        stats.hits++;
      } else {
        stats.misses++;
      }
    }
  }
  const byStation: StationStats[] = [];
  for (let station = 1; station <= 5; station++) {
    const stats = stationStatsMap.get(station)!;
    const stationTotal = stats.hits + stats.misses;
    byStation.push({
      station,
      hits: stats.hits,
      misses: stats.misses,
      total: stationTotal,
      percentage: stationTotal > 0 ? Math.round((stats.hits / stationTotal) * 100) : 0,
    });
  }

  // Calculate trajectory stats
  const trajectories: Trajectory[] = ['hard-left', 'left', 'middle', 'right', 'hard-right'];
  const trajectoryStatsMap = new Map<Trajectory, { hits: number; misses: number }>();
  for (const traj of trajectories) {
    trajectoryStatsMap.set(traj, { hits: 0, misses: 0 });
  }
  for (const shot of allShots) {
    const stats = trajectoryStatsMap.get(shot.trajectory);
    if (stats) {
      if (shot.result === 'hit') {
        stats.hits++;
      } else {
        stats.misses++;
      }
    }
  }
  const byTrajectory: TrajectoryStats[] = [];
  for (const traj of trajectories) {
    const stats = trajectoryStatsMap.get(traj)!;
    const trajTotal = stats.hits + stats.misses;
    byTrajectory.push({
      trajectory: traj,
      hits: stats.hits,
      misses: stats.misses,
      total: trajTotal,
      percentage: trajTotal > 0 ? Math.round((stats.hits / trajTotal) * 100) : 0,
    });
  }

  return {
    startDate: allShots[0]?.timestamp || Date.now(),
    endDate: allShots[allShots.length - 1]?.timestamp || Date.now(),
    totalShots: total,
    hits,
    misses,
    percentage: total > 0 ? Math.round((hits / total) * 100) : 0,
    byStation,
    byTrajectory,
  };
};

export const getPlayerPracticeStats = getPlayerEventStats;

// Team Invite operations
export const createTeamInvite = async (
  teamId: string,
  createdByProfileId: string,
  code: string,
  role: string = 'player'
) => {
  const { data, error } = await supabase
    .from('team_invites')
    .insert([{
      team_id: teamId,
      created_by: createdByProfileId,
      code,
      role,
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getTeamInviteByCode = async (code: string) => {
  // Use SECURITY DEFINER RPC to bypass RLS and get invite + team name
  // in a single query. The invite code acts as authentication.
  const { data, error } = await supabase.rpc('get_invite_by_code', {
    invite_code: code,
  });

  if (error) return null;

  return data;
};

export const incrementInviteUseCount = async (inviteId: string, currentCount: number) => {
  const { error } = await supabase
    .from('team_invites')
    .update({ use_count: currentCount + 1 })
    .eq('id', inviteId);

  if (error) throw error;
};

export const addTeamMember = async (
  teamId: string,
  profileId: string,
  role: string = 'player'
) => {
  const { data, error } = await supabase
    .from('team_members')
    .insert([{
      team_id: teamId,
      profile_id: profileId,
      role,
      status: 'active',
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const isTeamMember = async (teamId: string, profileId: string): Promise<boolean> => {
  const { data } = await supabase
    .from('team_members')
    .select('id')
    .eq('team_id', teamId)
    .eq('profile_id', profileId)
    .eq('status', 'active')
    .single();

  return !!data;
};

// Team membership & admin operations
export const getCurrentTeamMember = async (
  teamId: string,
  profileId: string
): Promise<{ id: string; role: string; isAdmin: boolean; isHeadCoach: boolean } | null> => {
  const { data, error } = await supabase
    .from('team_members')
    .select('id, role, is_admin, is_head_coach')
    .eq('team_id', teamId)
    .eq('profile_id', profileId)
    .eq('status', 'active')
    .single();

  if (error || !data) return null;

  return {
    id: data.id,
    role: data.role,
    isAdmin: data.is_admin ?? false,
    isHeadCoach: data.is_head_coach ?? false,
  };
};

export const updateTeamName = async (teamId: string, newName: string): Promise<void> => {
  const { error } = await supabase
    .from('teams')
    .update({ name: newName })
    .eq('id', teamId);

  if (error) throw error;
};

export const deleteTeam = async (teamId: string): Promise<void> => {
  const { error } = await supabase
    .from('teams')
    .delete()
    .eq('id', teamId);

  if (error) throw error;
};

export const getAllTeamMembers = async (teamId: string): Promise<Array<{
  id: string;
  profileId: string;
  role: string;
  isAdmin: boolean;
  isHeadCoach: boolean;
  displayName: string;
}>> => {
  const { data, error } = await supabase
    .from('team_members')
    .select('id, profile_id, role, is_admin, is_head_coach, profiles!inner(display_name)')
    .eq('team_id', teamId)
    .eq('status', 'active');

  if (error) throw error;

  return (data || []).map(member => ({
    id: member.id,
    profileId: member.profile_id,
    role: member.role,
    isAdmin: member.is_admin ?? false,
    isHeadCoach: member.is_head_coach ?? false,
    displayName: (member.profiles as any).display_name,
  }));
};

export const setTeamMemberAdmin = async (memberId: string, isAdmin: boolean): Promise<void> => {
  const { error } = await supabase
    .from('team_members')
    .update({ is_admin: isAdmin })
    .eq('id', memberId);

  if (error) throw error;
};

export const setHeadCoach = async (teamId: string, newHeadCoachMemberId: string): Promise<void> => {
  // First, unset the current head coach (if any)
  const { error: unsetError } = await supabase
    .from('team_members')
    .update({ is_head_coach: false })
    .eq('team_id', teamId)
    .eq('is_head_coach', true);

  if (unsetError) throw unsetError;

  // Then set the new head coach
  const { error: setError } = await supabase
    .from('team_members')
    .update({ is_head_coach: true })
    .eq('id', newHeadCoachMemberId);

  if (setError) throw setError;
};

// Multi-scorer operations
export const setScorerCode = async (eventId: string, code: string): Promise<void> => {
  const { error } = await supabase
    .from('events')
    .update({ scorer_code: code })
    .eq('id', eventId);

  if (error) throw error;
};

export const getEventByScorerCode = async (code: string): Promise<Event | undefined> => {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('scorer_code', code)
    .single();

  if (error || !data) return undefined;

  return mapEvent(data);
};

// Leaderboard data: get all player scores for an event (single query with joins)
export const getEventLeaderboard = async (eventId: string): Promise<Array<{
  playerId: string;
  playerName: string;
  totalShots: number;
  hits: number;
  percentage: number;
}>> => {
  // Single denormalized query: shots → player_rounds → rounds → squads → event
  // plus shots → player_rounds → squad_players → players for names
  const { data: shots, error } = await supabase
    .from('shots')
    .select(`
      result,
      player_rounds!inner(
        squad_players!inner(
          player_id,
          players!inner(name)
        ),
        rounds!inner(
          squads!inner(event_id)
        )
      )
    `)
    .eq('player_rounds.rounds.squads.event_id', eventId);

  if (error || !shots || shots.length === 0) return [];

  // Aggregate by player
  const playerAgg = new Map<string, { name: string; hits: number; total: number }>();

  for (const shot of shots as any[]) {
    const squadPlayer = shot.player_rounds?.squad_players;
    if (!squadPlayer) continue;

    const playerId = squadPlayer.player_id;
    const playerName = squadPlayer.players?.name || 'Unknown';

    const existing = playerAgg.get(playerId) || { name: playerName, hits: 0, total: 0 };
    existing.total++;
    if (shot.result === 'hit') existing.hits++;
    playerAgg.set(playerId, existing);
  }

  return Array.from(playerAgg.entries())
    .map(([playerId, agg]) => ({
      playerId,
      playerName: agg.name,
      totalShots: agg.total,
      hits: agg.hits,
      percentage: agg.total > 0 ? Math.round((agg.hits / agg.total) * 100) : 0,
    }))
    .sort((a, b) => b.percentage - a.percentage || b.hits - a.hits);
};

// Auto-expire stale events
export const autoExpireStaleEvents = async (teamId: string, maxInactiveHours = 8): Promise<number> => {
  const activeEvents = await getActiveEventsByTeam(teamId);
  if (!activeEvents.length) return 0;

  const cutoffMs = Date.now() - maxInactiveHours * 60 * 60 * 1000;
  let expired = 0;

  for (const event of activeEvents) {
    try {
      // Get squads for this event
      const { data: squads } = await supabase
        .from('squads')
        .select('id')
        .eq('event_id', event.id);

      let lastActivityMs = event.startTime;

      if (squads && squads.length > 0) {
        const squadIds = squads.map(s => s.id);

        // Get squad_players for those squads
        const { data: squadPlayers } = await supabase
          .from('squad_players')
          .select('id')
          .in('squad_id', squadIds);

        if (squadPlayers && squadPlayers.length > 0) {
          const squadPlayerIds = squadPlayers.map(sp => sp.id);

          // Get player_rounds for those squad_players
          const { data: playerRounds } = await supabase
            .from('player_rounds')
            .select('id')
            .in('squad_player_id', squadPlayerIds);

          if (playerRounds && playerRounds.length > 0) {
            const playerRoundIds = playerRounds.map(pr => pr.id);

            // Get the most recent shot across all player_rounds
            const { data: latestShot } = await supabase
              .from('shots')
              .select('timestamp')
              .in('player_round_id', playerRoundIds)
              .order('timestamp', { ascending: false })
              .limit(1);

            if (latestShot && latestShot.length > 0) {
              lastActivityMs = new Date(latestShot[0].timestamp).getTime();
            }
          }
        }
      }

      if (lastActivityMs < cutoffMs) {
        await endEvent(event.id);
        expired++;
      }
    } catch {
      // Skip this event on error, continue with others
    }
  }

  return expired;
};

// Bulk player creation
export const createPlayersBulk = async (
  players: Array<{ name: string; dateOfBirth: string; classification?: PlayerClassification }>,
  profileId: string,
  teamId?: string
): Promise<Player[]> => {
  const rows = players.map(p => ({
    profile_id: profileId,
    name: p.name,
    date_of_birth: p.dateOfBirth || null,
    team_id: teamId || null,
    classification: p.classification || null,
  }));

  const { data, error } = await supabase
    .from('players')
    .insert(rows)
    .select();

  if (error) throw error;

  return (data || []).map(mapPlayer);
};

// Coaching tip operations
export const updateShotCoachingTip = async (shotId: string, tip: string): Promise<void> => {
  const { error } = await supabase
    .from('shots')
    .update({ coaching_tip: tip })
    .eq('id', shotId);

  if (error) throw error;
};

export const getCoachingTipsByPlayer = async (playerId: string): Promise<Array<{
  tip: string;
  count: number;
  lastNoted: number;
}>> => {
  // Get squad_players for this player
  const { data: squadPlayers } = await supabase
    .from('squad_players')
    .select('id')
    .eq('player_id', playerId);

  if (!squadPlayers || squadPlayers.length === 0) return [];

  const squadPlayerIds = squadPlayers.map(sp => sp.id);

  // Get player_rounds for those squad_players
  const { data: playerRounds } = await supabase
    .from('player_rounds')
    .select('id')
    .in('squad_player_id', squadPlayerIds);

  if (!playerRounds || playerRounds.length === 0) return [];

  const playerRoundIds = playerRounds.map(pr => pr.id);

  // Get all shots with coaching tips
  const { data: shots, error } = await supabase
    .from('shots')
    .select('coaching_tip, timestamp')
    .in('player_round_id', playerRoundIds)
    .not('coaching_tip', 'is', null)
    .order('timestamp', { ascending: false });

  if (error || !shots || shots.length === 0) return [];

  // Aggregate by tip
  const tipMap = new Map<string, { count: number; lastNoted: number }>();

  for (const shot of shots) {
    const tip = shot.coaching_tip;
    if (!tip) continue;

    const existing = tipMap.get(tip);
    if (existing) {
      existing.count++;
    } else {
      tipMap.set(tip, {
        count: 1,
        lastNoted: new Date(shot.timestamp).getTime(),
      });
    }
  }

  return Array.from(tipMap.entries())
    .map(([tip, data]) => ({
      tip,
      count: data.count,
      lastNoted: data.lastNoted,
    }))
    .sort((a, b) => b.count - a.count);
};

// Scheduled event operations
export const startScheduledEvent = async (eventId: string): Promise<void> => {
  const { error } = await supabase
    .from('events')
    .update({ status: 'active', start_time: new Date().toISOString() })
    .eq('id', eventId);

  if (error) throw error;
};

export const getScheduledEventsByTeam = async (teamId: string): Promise<Event[]> => {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('team_id', teamId)
    .eq('status', 'scheduled')
    .order('scheduled_start', { ascending: true });

  if (error) throw error;

  return data.map(mapEvent);
};

// Event code operations
export const setEventCode = async (eventId: string, code: string): Promise<void> => {
  const { error } = await supabase
    .from('events')
    .update({ event_code: code })
    .eq('id', eventId);

  if (error) throw error;
};

export const getEventByEventCode = async (code: string): Promise<{ event: any; teamName: string } | null> => {
  const { data, error } = await supabase.rpc('get_event_by_event_code', {
    p_code: code,
  });

  if (error || !data || data.length === 0) return null;

  const row = data[0];
  return {
    event: {
      id: row.id,
      teamId: row.team_id,
      name: row.name,
      discipline: row.discipline,
      variant: row.variant,
      location: row.location,
      status: row.status,
      scheduledStart: row.scheduled_start ? new Date(row.scheduled_start).getTime() : null,
      startTime: row.start_time ? new Date(row.start_time).getTime() : 0,
      createdBy: row.created_by,
    },
    teamName: row.team_name,
  };
};

// Event participant operations
export const addEventParticipant = async (
  eventId: string,
  profileId: string,
  playerId?: string
): Promise<void> => {
  const { error } = await supabase
    .from('event_participants')
    .insert([{
      event_id: eventId,
      profile_id: profileId,
      player_id: playerId || null,
      joined_via_code: true,
    }]);

  if (error) throw error;
};

export const isEventParticipant = async (eventId: string, profileId: string): Promise<boolean> => {
  const { data } = await supabase
    .from('event_participants')
    .select('id')
    .eq('event_id', eventId)
    .eq('profile_id', profileId)
    .single();

  return !!data;
};

// Player follow code operations

export const createPlayerFollowCode = async (
  playerId: string,
  createdByProfileId: string,
  code: string
) => {
  const { data, error } = await supabase
    .from('player_follow_codes')
    .insert([{
      player_id: playerId,
      created_by: createdByProfileId,
      code,
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getFollowCodeByCode = async (code: string) => {
  const { data, error } = await supabase.rpc('get_follow_code_by_code', {
    follow_code: code,
  });

  if (error) return null;
  return data;
};

export const getFollowCodesByPlayer = async (playerId: string) => {
  const { data, error } = await supabase
    .from('player_follow_codes')
    .select('*')
    .eq('player_id', playerId)
    .eq('is_active', true)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const deactivateFollowCode = async (codeId: string) => {
  const { error } = await supabase
    .from('player_follow_codes')
    .update({ is_active: false })
    .eq('id', codeId);

  if (error) throw error;
};

export const incrementFollowCodeUseCount = async (codeId: string, currentCount: number) => {
  const { error } = await supabase
    .from('player_follow_codes')
    .update({ use_count: currentCount + 1 })
    .eq('id', codeId);

  if (error) throw error;
};

// Player follow operations

export const createPlayerFollow = async (followerProfileId: string, playerId: string) => {
  const { data, error } = await supabase
    .from('player_follows')
    .insert([{
      follower_profile_id: followerProfileId,
      player_id: playerId,
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getFollowedPlayers = async (followerProfileId: string) => {
  const { data, error } = await supabase
    .from('player_follows')
    .select(`
      id,
      player_id,
      followed_at,
      players (
        id,
        name,
        classification,
        profile_id
      )
    `)
    .eq('follower_profile_id', followerProfileId)
    .order('followed_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const isFollowingPlayer = async (followerProfileId: string, playerId: string): Promise<boolean> => {
  const { data } = await supabase
    .from('player_follows')
    .select('id')
    .eq('follower_profile_id', followerProfileId)
    .eq('player_id', playerId)
    .single();

  return !!data;
};

export const unfollowPlayer = async (followerProfileId: string, playerId: string) => {
  const { error } = await supabase
    .from('player_follows')
    .delete()
    .eq('follower_profile_id', followerProfileId)
    .eq('player_id', playerId);

  if (error) throw error;
};

// Field operations and development tools
export const getProfileNames = async (profileIds: string[]): Promise<Record<string, string>> => {
  const ids = [...new Set(profileIds.filter(Boolean))];
  if (ids.length === 0) return {};
  const { data, error } = await supabase.from('profiles').select('id, display_name').in('id', ids);
  if (error) return {};
  return Object.fromEntries((data || []).map(profile => [profile.id, profile.display_name]));
};

export const getTeamInvites = async (teamId: string) => {
  const { data, error } = await supabase
    .from('team_invites')
    .select('*')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const markTeamInviteShared = async (inviteId: string): Promise<void> => {
  const { error } = await supabase.from('team_invites').update({ last_shared_at: new Date().toISOString() }).eq('id', inviteId);
  if (error) throw error;
};

export const deactivateTeamInvite = async (inviteId: string): Promise<void> => {
  const { error } = await supabase.from('team_invites').update({ is_active: false }).eq('id', inviteId);
  if (error) throw error;
};

export const createPlayerClaim = async (playerId: string, createdBy: string, code: string) => {
  const { data, error } = await supabase
    .from('player_claims')
    .insert([{ player_id: playerId, created_by: createdBy, code }])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const getActivePlayerClaim = async (playerId: string) => {
  const { data, error } = await supabase
    .from('player_claims')
    .select('*')
    .eq('player_id', playerId)
    .eq('is_active', true)
    .is('claimed_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
};

export const claimPlayerProfile = async (code: string, profileId: string): Promise<string> => {
  const { data, error } = await supabase.rpc('claim_player_profile', { p_code: code, p_profile_id: profileId });
  if (error) throw error;
  return data;
};

export const getParentPlayerLinks = async (parentProfileId: string): Promise<ParentPlayerLink[]> => {
  const { data, error } = await supabase
    .from('parent_player_links')
    .select('*')
    .eq('parent_profile_id', parentProfileId)
    .order('created_at');
  if (error) throw error;
  return (data || []).map(link => ({
    id: link.id,
    parentProfileId: link.parent_profile_id,
    playerId: link.player_id,
    relationship: link.relationship,
    createdAt: new Date(link.created_at).getTime(),
  }));
};

export const addParentPlayerLink = async (parentProfileId: string, playerId: string, _createdBy?: string): Promise<void> => {
  const { error } = await supabase.rpc('link_athlete_to_profile', {
    p_parent_profile_id: parentProfileId,
    p_player_id: playerId,
  });
  if (error) throw error;
};

export const createLinkedAthlete = async (
  parentProfileId: string,
  teamId: string,
  name: string,
  dateOfBirth: string,
  classification?: PlayerClassification
): Promise<string> => {
  const { data, error } = await supabase.rpc('create_linked_athlete', {
    p_parent_profile_id: parentProfileId,
    p_team_id: teamId,
    p_name: name,
    p_date_of_birth: dateOfBirth,
    p_classification: classification || null,
  });
  if (error) throw error;
  return data;
};

export const removeParentPlayerLink = async (parentProfileId: string, playerId: string): Promise<void> => {
  const { error } = await supabase.from('parent_player_links').delete().eq('parent_profile_id', parentProfileId).eq('player_id', playerId);
  if (error) throw error;
};

const mapGoal = (goal: any): PlayerGoal => ({
  id: goal.id,
  playerId: goal.player_id,
  title: goal.title,
  targetType: goal.target_type,
  targetValue: goal.target_value === null ? undefined : Number(goal.target_value),
  station: goal.station ?? undefined,
  status: goal.status,
  createdBy: goal.created_by || undefined,
  createdAt: new Date(goal.created_at).getTime(),
  completedAt: goal.completed_at ? new Date(goal.completed_at).getTime() : null,
});

export const getPlayerGoals = async (playerId: string): Promise<PlayerGoal[]> => {
  const { data, error } = await supabase.from('player_goals').select('*').eq('player_id', playerId).neq('status', 'archived').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(mapGoal);
};

export const createPlayerGoal = async (goal: Omit<PlayerGoal, 'id' | 'createdAt' | 'status' | 'completedAt'>): Promise<PlayerGoal> => {
  const { data, error } = await supabase.from('player_goals').insert([{
    player_id: goal.playerId,
    title: goal.title,
    target_type: goal.targetType,
    target_value: goal.targetValue ?? null,
    station: goal.station ?? null,
    created_by: goal.createdBy || null,
  }]).select().single();
  if (error) throw error;
  return mapGoal(data);
};

export const updatePlayerGoalStatus = async (goalId: string, status: PlayerGoal['status']): Promise<void> => {
  const { error } = await supabase.from('player_goals').update({ status, completed_at: status === 'completed' ? new Date().toISOString() : null }).eq('id', goalId);
  if (error) throw error;
};

const mapCoachingNote = (note: any): CoachingNote => ({
  id: note.id,
  playerId: note.player_id,
  eventId: note.event_id || undefined,
  body: note.body,
  createdBy: note.created_by || undefined,
  createdByName: note.profiles?.display_name || undefined,
  createdAt: new Date(note.created_at).getTime(),
});

export const getPlayerCoachingNotes = async (playerId: string): Promise<CoachingNote[]> => {
  const { data, error } = await supabase
    .from('coaching_notes')
    .select('*, profiles:created_by(display_name)')
    .eq('player_id', playerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(mapCoachingNote);
};

export const createCoachingNote = async (playerId: string, body: string, createdBy: string, eventId?: string): Promise<CoachingNote> => {
  const { data, error } = await supabase.from('coaching_notes').insert([{
    player_id: playerId,
    event_id: eventId || null,
    body,
    created_by: createdBy,
  }]).select('*').single();
  if (error) throw error;
  return mapCoachingNote(data);
};

export const getSavedSquads = async (teamId: string): Promise<SavedSquad[]> => {
  const { data, error } = await supabase
    .from('saved_squads')
    .select('*, saved_squad_players(player_id, station, position)')
    .eq('team_id', teamId)
    .order('name');
  if (error) throw error;
  return (data || []).map(squad => ({
    id: squad.id,
    teamId: squad.team_id,
    name: squad.name,
    players: (squad.saved_squad_players || []).map((player: any) => ({ playerId: player.player_id, station: player.station, position: player.position })).sort((a: any, b: any) => a.position - b.position),
    createdAt: new Date(squad.created_at).getTime(),
    updatedAt: new Date(squad.updated_at).getTime(),
  }));
};

export const saveSquadTemplate = async (teamId: string, name: string, createdBy: string, players: Array<{ playerId: string; station: number }>): Promise<void> => {
  const { data, error } = await supabase.from('saved_squads').upsert([{
    team_id: teamId,
    name,
    created_by: createdBy,
    updated_at: new Date().toISOString(),
  }], { onConflict: 'team_id,name' }).select('id').single();
  if (error) throw error;
  const { error: deleteError } = await supabase.from('saved_squad_players').delete().eq('saved_squad_id', data.id);
  if (deleteError) throw deleteError;
  if (players.length > 0) {
    const { error: playerError } = await supabase.from('saved_squad_players').insert(players.map((player, position) => ({
      saved_squad_id: data.id,
      player_id: player.playerId,
      station: player.station,
      position,
    })));
    if (playerError) throw playerError;
  }
};

export const deleteSavedSquad = async (savedSquadId: string): Promise<void> => {
  const { error } = await supabase.from('saved_squads').delete().eq('id', savedSquadId);
  if (error) throw error;
};

export const getRecentShooterIds = async (teamId: string, limit = 5): Promise<string[]> => {
  const { data, error } = await supabase
    .from('squad_players')
    .select('player_id, squads!inner(events!inner(team_id, start_time))')
    .eq('squads.events.team_id', teamId)
    .order('created_at', { ascending: false })
    .limit(limit * 3);
  if (error) return [];
  return [...new Set((data || []).map(row => row.player_id))].slice(0, limit);
};

export const getAdminActivity = async (teamId: string, limit = 30): Promise<AdminActivity[]> => {
  const { data, error } = await supabase
    .from('admin_activity_log')
    .select('*, profiles:actor_profile_id(display_name)')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map(activity => ({
    id: activity.id,
    teamId: activity.team_id || undefined,
    actorProfileId: activity.actor_profile_id || undefined,
    actorName: activity.profiles?.display_name || undefined,
    action: activity.action,
    targetType: activity.target_type,
    targetId: activity.target_id || undefined,
    details: activity.details || {},
    createdAt: new Date(activity.created_at).getTime(),
  }));
};

export const getScoringFeedbackPreference = async (profileId: string): Promise<ScoringFeedback> => {
  const { data, error } = await supabase.from('profile_preferences').select('scoring_feedback').eq('profile_id', profileId).maybeSingle();
  if (error || !data) return 'vibrate';
  return data.scoring_feedback as ScoringFeedback;
};

export const setScoringFeedbackPreference = async (profileId: string, scoringFeedback: ScoringFeedback): Promise<void> => {
  const { error } = await supabase.from('profile_preferences').upsert([{
    profile_id: profileId,
    scoring_feedback: scoringFeedback,
    updated_at: new Date().toISOString(),
  }], { onConflict: 'profile_id' });
  if (error) throw error;
};

export const getShooterStatsSummary = async (playerId: string, since?: number) => {
  const { data, error } = await supabase.rpc('get_shooter_stats_summary', {
    p_player_id: playerId, p_since: since ? new Date(since).toISOString() : null,
  });
  if (error) throw error;
  return { ...data, events: (data.events || []).map(mapEvent) };
};
